(() => {
  'use strict';

  const ERROR_CLASS = 'product-option--error';
  const ERROR_MESSAGE_CLASS = 'product-option-validation__message';
  const REQUIRED_GROUP_SELECTOR = [
    '.gpo-element:has(.gpo-label.required)',
    '.gpo-element[data-element-id="image-swatches-2"]',
    '.holster-selector-field[data-required="true"]',
    'custom-product-field[data-required="true"]',
    '.field-wrapper[data-required="true"]'
  ].join(',');
  const CONTROL_SELECTOR = [
    'select:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"]):not([type="submit"]):not([type="button"])'
  ].join(',');
  const ATC_SELECTOR = [
    '.main-product-atc',
    '.custom-atc-btn',
    '[id^="StickyAtcSubmitButton-"]',
    '[data-gpo-product-atc]',
    'form[action*="/cart/add"] [name="add"]',
    'form[action*="/cart/add"] [type="submit"]'
  ].join(',');

  let lastScrollAt = 0;
  let validationQueued = false;

  function isVisible(element) {
    if (!element || element.hidden || element.closest('[hidden], .gpo-hidden')) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  function getControls(group) {
    // Choice inputs in Globo are visually hidden and controlled by their labels.
    // The group visibility, rather than the native input visibility, determines
    // whether the option currently needs validation.
    return Array.from(group.querySelectorAll(CONTROL_SELECTOR));
  }

  function hasValue(control, controls) {
    if (control.type === 'radio') {
      return controls.some((item) => item.type === 'radio' && item.name === control.name && item.checked);
    }
    if (control.type === 'checkbox') {
      const checkboxes = controls.filter((item) => item.type === 'checkbox');
      return checkboxes.some((item) => item.checked);
    }
    if (control.type === 'file') return Boolean(control.files && control.files.length);
    return String(control.value || '').trim() !== '';
  }

  function isGroupValid(group) {
    const controls = getControls(group);
    if (!controls.length) return true;

    const choice = controls.find((control) => control.type === 'radio' || control.type === 'checkbox');
    if (choice) return hasValue(choice, controls);

    return controls.every((control) => hasValue(control, controls));
  }

  function getRequiredGroups() {
    const groups = new Set(
      Array.from(document.querySelectorAll(REQUIRED_GROUP_SELECTOR)).filter(isVisible)
    );

    document
      .querySelectorAll('[required], [aria-required="true"]')
      .forEach((control) => {
        if (!isVisible(control)) return;
        const group = control.closest(
          '.gpo-element, custom-product-field, .field-wrapper, .product-form__input'
        );
        if (group) groups.add(group);
      });

    return Array.from(groups);
  }

  function setGroupError(group, show) {
    group.classList.toggle(ERROR_CLASS, show);

    getControls(group).forEach((control) => {
      if (show) control.setAttribute('aria-invalid', 'true');
      else control.removeAttribute('aria-invalid');
    });

    if (show) {
      group.setAttribute('role', 'group');
      if (!group.hasAttribute('tabindex')) group.setAttribute('tabindex', '-1');

      const hasVisibleMessage = Array.from(
        group.querySelectorAll('.messages .error, .error-message, .input-error-msg')
      ).some((message) => isVisible(message) && String(message.textContent || '').trim());

      if (!hasVisibleMessage && !group.querySelector(`.${ERROR_MESSAGE_CLASS}`)) {
        const message = document.createElement('p');
        message.className = ERROR_MESSAGE_CLASS;
        message.setAttribute('role', 'alert');
        message.textContent = 'This field is required';
        group.appendChild(message);
      }
    } else {
      group.querySelector(`.${ERROR_MESSAGE_CLASS}`)?.remove();
    }
  }

  function validateAllGroups() {
    return getRequiredGroups().filter((group) => {
      const invalid = !isGroupValid(group);
      setGroupError(group, invalid);
      return invalid;
    });
  }

  function firstVisibleAppError() {
    return Array.from(
      document.querySelectorAll(
        `.gpo-element .messages .error, .error-message, .input-error-msg, .${ERROR_MESSAGE_CLASS}`
      )
    ).find((message) => {
      if (!isVisible(message) || !String(message.textContent || '').trim()) return false;
      const styles = window.getComputedStyle(message);
      return styles.opacity !== '0';
    });
  }

  function syncAppErrors() {
    document.querySelectorAll('.gpo-element').forEach((group) => {
      const message = group.querySelector('.messages .error');
      if (message && isVisible(message) && String(message.textContent || '').trim()) {
        setGroupError(group, true);
      }
    });
  }

  function scrollToGroup(group) {
    const now = Date.now();
    if (!group || now - lastScrollAt < 400) return;
    lastScrollAt = now;

    group.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center'
    });

    window.setTimeout(() => {
      group.focus({ preventScroll: true });
    }, 450);
  }

  function validateAndScroll() {
    validationQueued = false;
    const invalidGroups = validateAllGroups();
    syncAppErrors();

    const appError = firstVisibleAppError();
    const errorGroup = appError && appError.closest(
      '.gpo-element, .holster-selector-field, custom-product-field, .field-wrapper, .product-form__input'
    );
    scrollToGroup(invalidGroups[0] || errorGroup);
  }

  function queueValidation() {
    if (validationQueued) return;
    validationQueued = true;
    window.setTimeout(validateAndScroll, 0);
    window.setTimeout(syncAppErrors, 150);
  }

  function isProductAddToCart(target) {
    if (!(target instanceof Element)) return false;
    const button = target.closest(ATC_SELECTOR);
    if (!button || button.closest('cart-drawer, cart-notification')) return false;

    const form = button.form || button.closest('form');
    return (
      button.matches(
        '.main-product-atc, .custom-atc-btn, [id^="StickyAtcSubmitButton-"], [data-gpo-product-atc]'
      ) ||
      Boolean(form && form.matches('[action*="/cart/add"]'))
    );
  }

  document.addEventListener('click', (event) => {
    if (!isProductAddToCart(event.target)) return;

    const invalidGroups = validateAllGroups();
    if (invalidGroups.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToGroup(invalidGroups[0]);
      return;
    }

    queueValidation();
  }, true);

  document.addEventListener('submit', (event) => {
    if (
      event.target.matches('form[action*="/cart/add"]') &&
      !event.target.closest('cart-drawer, cart-notification')
    ) {
      const invalidGroups = validateAllGroups();
      if (invalidGroups.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        scrollToGroup(invalidGroups[0]);
        return;
      }

      queueValidation();
    }
  }, true);

  document.addEventListener('input', (event) => {
    const group = event.target.closest(`.${ERROR_CLASS}`);
    if (group && isGroupValid(group)) setGroupError(group, false);
  });

  document.addEventListener('change', (event) => {
    const group = event.target.closest(
      '.gpo-element, .holster-selector-field, custom-product-field[data-required="true"], .field-wrapper[data-required="true"]'
    );
    if (group) setGroupError(group, !isGroupValid(group));
  });

  document.addEventListener('gpoRenderCompleted', syncAppErrors);
  document.addEventListener('shopify:section:load', syncAppErrors);

  const observer = new MutationObserver((mutations) => {
    const shouldSync = mutations.some((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE
        ? mutation.target
        : mutation.target.parentElement;
      if (target && target.closest('.messages, .error-message, .input-error-msg')) return true;

      return Array.from(mutation.addedNodes).some((node) => (
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches('.gpo-container, .error, .error-message') ||
          node.querySelector('.gpo-container, .messages .error, .error-message'))
      ));
    });

    if (shouldSync) window.requestAnimationFrame(syncAppErrors);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
